import {
  CooldownDurationAbi,
  Erc4626ReadAbi,
  LockupPeriodAbi,
  TotalSupplyAbi,
} from '../abis/shared'
import type { SavingsRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type SavingsReader } from './shared'

/**
 * Plain ERC-4626 plus a live read of the StakedUSDeV2 exit lock.
 *
 * Identical to [`readerErc4626`](./erc4626.ts) in every respect but a fourth
 * call, `cooldownDuration()`, whose value overrides the registry's pinned
 * `withdrawalCooldownSeconds` (see `SavingsOnChainState`). The vault stays a
 * conforming 4626 — `isErc4626` is true for this reader.
 *
 * It exists because a pinned cooldown is a snapshot of an owner-settable dial
 * with no timelock, and on the entry that motivated it the pin and the chain
 * disagree by three orders of magnitude: Brix documents wiTRY at 3 days and
 * the contract answered 300 seconds on 2026-08-14 (pre-launch), with
 * `MAX_COOLDOWN_DURATION` allowing 90 days. Both directions of that error are
 * real — advertising a 5-minute exit that becomes 3 days strands capital, and
 * advertising 3 days on a vault that exits in 5 minutes buries liquidity that
 * is actually there.
 *
 * A `0` answer is a real state, not a failure: StakedUSDeV2 with the cooldown
 * disabled IS a vanilla 4626 whose `withdraw`/`redeem` work directly. It
 * passes through as `0`, so the row reports no lock rather than falling back
 * to the registry's pin.
 *
 * The getter is per-entry (`cooldownGetter`), because the lock is the same
 * FACT under different names: StakedUSDeV2 forks call it `cooldownDuration`,
 * Theo's sthUSD calls it `lockupPeriod`. Both are owner-settable dials with no
 * timelock, both gate the same exit, and both belong in the same field — so
 * they share this reader rather than forking it for a spelling.
 *
 * The OTHER cooldown-shaped rows (Ethena sUSDe, Tori strUSD, Neutrl sNUSD,
 * Avant savETH) still pin their value and are untouched here — they are
 * launched vaults whose documented and live durations agree. Migrating them
 * to this reader is a follow-up, not a prerequisite.
 */
export const readerErc4626Cooldown = (
  entry: SavingsRegistryEntry,
): SavingsReader => {
  const shareUnit = 10n ** BigInt(entry.decimals)
  const underlyingUnit =
    10n ** BigInt(entry.underlyingDecimals ?? entry.decimals)
  const cooldownGetter = entry.cooldownGetter ?? 'cooldownDuration'
  const cooldownAbi =
    cooldownGetter === 'lockupPeriod' ? LockupPeriodAbi : CooldownDurationAbi
  return {
    calls: [
      { address: entry.address, name: 'totalAssets', params: [] },
      { address: entry.address, name: 'totalSupply', params: [] },
      { address: entry.address, name: 'convertToAssets', params: [shareUnit] },
      { address: entry.address, name: cooldownGetter, params: [] },
    ],
    abis: [Erc4626ReadAbi, TotalSupplyAbi, Erc4626ReadAbi, cooldownAbi],
    parse: ([assets, supply, rate, cooldown]) => {
      const totalAssets = toBigInt(assets)
      const totalSupply = toBigInt(supply)
      const convertToAssetsRaw = toBigInt(rate)
      if (
        totalAssets === undefined ||
        totalSupply === undefined ||
        convertToAssetsRaw === undefined
      ) {
        return undefined
      }
      // A failed cooldown read falls back to the registry pin downstream
      // (`state.withdrawalCooldownSeconds ?? entry.withdrawalCooldownSeconds`)
      // rather than dropping the whole entry — the exit lock is the one field
      // here that has a curated answer.
      const cooldownSecs = toBigInt(cooldown)
      return {
        totalAssets,
        totalSupply,
        exchangeRate: (convertToAssetsRaw * ONE_E18) / underlyingUnit,
        withdrawalCooldownSeconds:
          cooldownSecs === undefined ? undefined : Number(cooldownSecs),
      }
    },
  }
}
